/**
 * Modal de Captura de Leads — DataFlow Enterprise (Light)
 * Poka-Yoke: qualifica decisores + capacidade antes de liberar agenda.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, CheckCircle2, Loader2, Play, X } from 'lucide-react';
import {
  buildCalendlyEmbedSrc,
  CALENDLY_EMBED_URL,
  createEmptyLeadForm,
  EXECUTIVE_ROLE_OPTIONS,
  hasFormErrors,
  LeadFormData,
  LeadIntent,
  LeadSubmissionResult,
  submitLead,
  TEAM_SIZE_OPTIONS,
  validateLeadForm,
  type ExecutiveRole,
  type TeamSizeBand,
} from './leadQualification';

type ModalStep = 'form' | 'qualified' | 'nurture';

export interface LeadCaptureModalProps {
  open: boolean;
  intent: LeadIntent;
  onClose: () => void;
}

const INPUT_CLASS =
  'w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm transition-colors placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/15';

const SELECT_CLASS = `${INPUT_CLASS} appearance-none`;

function ModalTitles({ intent, step }: { intent: LeadIntent; step: ModalStep }) {
  if (step === 'qualified') {
    return (
      <>
        <h2 id="lead-modal-title" className="text-lg font-semibold text-slate-950">
          Agende sua demonstração técnica
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Perfil qualificado — selecione o melhor horário com nossa equipe de engenharia de processos.
        </p>
      </>
    );
  }

  if (step === 'nurture') {
    return (
      <>
        <h2 id="lead-modal-title" className="text-lg font-semibold text-slate-950">
          Obrigado pelo interesse
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Nossa engenharia de processos analisará seus dados de capacidade e entrará em contato se necessário.
        </p>
      </>
    );
  }

  return (
    <>
      <h2 id="lead-modal-title" className="text-lg font-semibold text-slate-950">
        {intent === 'diagnosis' ? 'Solicitar Diagnóstico Operacional' : 'Agendar Demonstração Técnica'}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Preencha os dados corporativos para qualificação consultiva. Sem autoatendimento ou contratação automática.
      </p>
    </>
  );
}

function DemoVideoPlaceholder() {
  return (
    <div className="lp-demo-video relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      <div className="flex aspect-video flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm">
          <Play className="h-6 w-6 text-blue-600" strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">Demonstração DataFlow</p>
          <p className="mt-1 text-xs text-slate-500">
            Engenharia, simulação e governança de processos — visão executiva da plataforma
          </p>
        </div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400">
          Vídeo demonstrativo · Acesso mediante qualificação
        </p>
      </div>
    </div>
  );
}

export const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({ open, intent, onClose }) => {
  const [step, setStep] = useState<ModalStep>('form');
  const [form, setForm] = useState<LeadFormData>(() => createEmptyLeadForm(intent));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<LeadSubmissionResult | null>(null);

  const reset = useCallback(() => {
    setStep('form');
    setForm(createEmptyLeadForm(intent));
    setErrors({});
    setSubmitting(false);
    setResult(null);
  }, [intent]);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, intent, reset]);

  useEffect(() => {
    if (!open) return undefined;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, submitting, onClose]);

  const setField = <K extends keyof LeadFormData>(key: K, value: LeadFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: LeadFormData = { ...form, intent };
    const validation = validateLeadForm(payload);
    if (hasFormErrors(validation)) {
      setErrors(validation as Record<string, string>);
      return;
    }

    setSubmitting(true);
    try {
      const submission = await submitLead(payload);
      setResult(submission);
      setStep(submission.outcome === 'qualified' ? 'qualified' : 'nurture');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const calendlySrc = result ? buildCalendlyEmbedSrc(CALENDLY_EMBED_URL, result.lead) : '';

  return (
    <div className="lp-lead-modal fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        aria-label="Fechar modal"
        onClick={submitting ? undefined : onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-modal-title"
        className="lp-lead-modal-panel relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200/80 px-6 py-5">
          <div className="min-w-0 flex-1">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
              <Calendar className="h-3.5 w-3.5 text-blue-600" strokeWidth={2} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Qualificação Consultiva
              </span>
            </div>
            <ModalTitles intent={intent} step={step} />
          </div>
          {!submitting && (
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md border border-slate-200 p-2 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {step === 'form' && (
            <form id="lead-capture-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="lead-fullName" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Nome Completo *
                  </label>
                  <input
                    id="lead-fullName"
                    type="text"
                    autoComplete="name"
                    value={form.fullName}
                    onChange={(e) => setField('fullName', e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Ex.: Ana Paula Mendes"
                  />
                  {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName}</p>}
                </div>

                <div>
                  <label htmlFor="lead-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    E-mail Corporativo *
                  </label>
                  <input
                    id="lead-email"
                    type="email"
                    autoComplete="work email"
                    value={form.corporateEmail}
                    onChange={(e) => setField('corporateEmail', e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="nome@empresa.com.br"
                  />
                  {errors.corporateEmail && (
                    <p className="mt-1 text-xs text-red-600">{errors.corporateEmail}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lead-role" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Cargo Executivo *
                  </label>
                  <select
                    id="lead-role"
                    value={form.executiveRole}
                    onChange={(e) => setField('executiveRole', e.target.value as ExecutiveRole | '')}
                    className={SELECT_CLASS}
                  >
                    <option value="">Selecione o cargo</option>
                    {EXECUTIVE_ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.executiveRole && (
                    <p className="mt-1 text-xs text-red-600">{errors.executiveRole}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lead-company" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Nome da Empresa *
                  </label>
                  <input
                    id="lead-company"
                    type="text"
                    autoComplete="organization"
                    value={form.companyName}
                    onChange={(e) => setField('companyName', e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Razão social ou nome fantasia"
                  />
                  {errors.companyName && <p className="mt-1 text-xs text-red-600">{errors.companyName}</p>}
                </div>

                <div>
                  <label htmlFor="lead-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Telefone (WhatsApp) *
                  </label>
                  <input
                    id="lead-phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="(11) 99999-0000"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="lead-teamSize" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Tamanho da Equipe Operacional *
                  </label>
                  <select
                    id="lead-teamSize"
                    value={form.teamSize}
                    onChange={(e) => setField('teamSize', e.target.value as TeamSizeBand | '')}
                    className={SELECT_CLASS}
                  >
                    <option value="">Selecione a faixa de colaboradores</option>
                    {TEAM_SIZE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.teamSize && <p className="mt-1 text-xs text-red-600">{errors.teamSize}</p>}
                </div>
              </div>

              <p className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500">
                Seus dados serão utilizados exclusivamente para qualificação consultiva e contato por
                especialistas em engenharia de processos, em conformidade com a LGPD. Não há checkout,
                planos públicos ou contratação automática nesta etapa.
              </p>
            </form>
          )}

          {step === 'qualified' && result && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={2} />
                <p className="text-sm leading-relaxed text-emerald-900">
                  Perfil qualificado para agenda prioritária. Escolha abaixo o horário da demonstração
                  técnica com nossa equipe.
                </p>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <iframe
                  title="Agenda DataFlow — Calendly"
                  src={calendlySrc}
                  className="h-[min(420px,55vh)] w-full border-0"
                  loading="lazy"
                />
              </div>
              <p className="text-center text-xs text-slate-400">
                Integração de agenda corporativa · Calendly / HubSpot
              </p>
            </div>
          )}

          {step === 'nurture' && (
            <div className="space-y-5">
              <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" strokeWidth={2} />
                <p className="text-sm leading-relaxed text-slate-700">
                  Obrigado pelo interesse. Nossa engenharia de processos analisará seus dados de
                  capacidade e entrará em contato se necessário.
                </p>
              </div>
              <DemoVideoPlaceholder />
              <p className="text-center text-xs text-slate-400">
                Enquanto isso, conheça a visão executiva da plataforma no material demonstrativo acima.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200/80 bg-slate-50 px-6 py-4">
          {step === 'form' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="lead-capture-form"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Qualificando…
                  </>
                ) : (
                  <>
                    Enviar para Qualificação
                    <Calendar className="h-4 w-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
